import React, { useState, useEffect } from 'react';

import { languageController } from '../../../controllers/language/languageController';
import formLang from '../../../controllers/language/shared/form';
import { useForm } from '../../../hooks/useForm';
import { convertLangCodeToSingleLetter, pushToGTMDataLayer, handleAnalyticsTrackingCodes } from '../../../utils/helpers';
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';

import Button from '../../1_Atoms/Button/Button';
import Input from '../../1_Atoms/Forms/Input/Input';
import Select from '../../1_Atoms/Forms/Select/Select';
import Textarea from '../../1_Atoms/Forms/Textarea/Textarea';

import Form from '../../2_Molecules/Form/Form';
import FormGroup from '../../2_Molecules/FormGroup/FormGroup';
import FormConfirmation from '../../2_Molecules/FormConfirmation/FormConfirmation';
import FormContainer from '../../2_Molecules/FormContainer/FormContainer';

function ContactUs({ formExtraParams }) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formError, setFormError] = useState(null)
  const { language, formSubmitButtonText } = formExtraParams
  const [phoneValue, setPhoneValue] = useState('')
  const [validations, setValidations] = useState({
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    email: ['required', 'email'],
    postalcode: ['required', 'postalcode'],
    phone: ['phone'],
    vin: ['vin', 'isAlphaNumeric'],
    subject: ['required'],
    message: ['required'],
  })

  const initState = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    phone:'',
    preferredContact:'',
    vin: '',
    subject: '',
    message: '',
    language: convertLangCodeToSingleLetter(language),
  }

  const { handleSubmit, handleChange, data, errors, setData } = useForm({
    initState,
    validations,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const endPointUrl = getEndpointUrl('contact');

        // Remove whitespace in postalcode and phone
        if (data.postalcode) {
          data.postalcode = data.postalcode.replace(/ /g, "");
        }
        if(data.phone.length > 10){
          data.phone = data.phone.replace(/\D/g, '');
        }

        const res = await handleRequest('post', endPointUrl, { ...data, vin: data.vin ? data.vin : null });
        if (res.Success) {
          setIsSubmitted(true);
          pushToGTMDataLayer({ formevent: 'submit' });
          handleAnalyticsTrackingCodes(data, 'Form Submit', 'Contact Us', 'ContactUs');
          setData(initState)
        } else {
          setFormError(res.state ? res.state : res.Errors[0])
        }
      } catch (err) {
        console.log(err)
      }
    },
  })

  const { firstname, lastname, email, postalcode, phone, preferredContact, vin, subject, message } = data;

  useEffect(() => {
      setValidations(prevValidations => {
        const newValidations = { ...prevValidations }; // Create a new object copy
        if (phoneValue.length > 9) {
          // Add 'required' rule to preferredContact
          newValidations.preferredContact = ['required'];
        } else {
          // Remove preferredContact rule
          if (newValidations.preferredContact) {
            delete newValidations.preferredContact;
          }
        }
        return newValidations;
      });
    }, [phoneValue])

  return (
    <FormContainer
      isSubmitted={isSubmitted}
      confirmation={(
        <FormConfirmation
          heading={languageController.getCopy('contact', 'heading')}
          subHeading={languageController.getCopy('contact', 'subHeading')}
          onClick={() => setIsSubmitted(false)}
        />
      )}
      className="mz-contact-us"
    >
      <Form
        type="contact-us"
        onSubmit={handleSubmit}
        isDark={false}
        noValidate={true}
        requiredFieldsHelperText={true}
        errorMessage={formError}
      >
        <FormGroup md={2}>
          <Input
            type="text"
            name="firstname"
            label={languageController.getFormCopy('label', 'firstname')}
            id="contact-firstname"
            value={firstname}
            isRequired={true}
            errorMessage={errors.firstname}
            onChange={handleChange}
            maxLength={50}
          />
          <Input
            type="text"
            name="lastname"
            label={languageController.getFormCopy('label', 'lastname')}
            id="contact-lastname"
            value={lastname}
            isRequired={true}
            errorMessage={errors.lastname}
            onChange={handleChange}
            maxLength={50}
          />
        </FormGroup>
        <Input
          type="email"
          name="email"
          label={languageController.getFormCopy('label', 'email')}
          id="contact-email"
          value={email}
          isRequired={true}
          errorMessage={errors.email}
          onChange={handleChange}
          maxLength={320}
        />
        <FormGroup md={2}>
          <Input
            type="text"
            name="postalcode"
            label={languageController.getFormCopy('label', 'postalcode')}
            id="contact-postalcode"
            value={postalcode}
            isRequired={true}
            errorMessage={errors.postalcode}
            onChange={handleChange}
            maxLength={7}
          />
          <Input
            type="text"
            name="phone"
            label={languageController.getFormCopy('label', 'phone')}
            id="contact-phone"
            value={phone}
            isRequired={false}
            errorMessage={errors.phone}
            onChange={(e)=>{
              handleChange(e)
              if(phoneValue != e.target.value)setPhoneValue(e.target.value.replace(/\D/g, ''))
            }}
            maxLength={14}
          />
        </FormGroup>
        {phoneValue.length > 9 &&
          <FormGroup md={2}>
            <Select
              name="preferredContact"
              label={languageController.getFormCopy('label', 'preferredContact')}
              id="contact-preferredContact"
              value={preferredContact}
              isRequired={true}
              errorMessage={errors.preferredContact}
              onChange={handleChange}
              placeholder={languageController.getFormCopy('placeholder', 'preferredContact')}
              options={formLang.select.preferredContact.map(subject => ({
                value: subject[language],
                label: subject[language],
              }))}
            />
          </FormGroup>
        }
        <Input
          type="text"
          name="vin"
          label={languageController.getFormCopy('label', 'vin')}
          id="contact-vin"
          value={vin}
          isRequired={false}
          errorMessage={errors.vin}
          onChange={handleChange}
          maxLength={17}
        />
        <Select
          name="subject"
          label={languageController.getFormCopy('label', 'subject')}
          id="contact-email-subject"
          value={subject}
          isRequired={true}
          errorMessage={errors.subject}
          onChange={handleChange}
          placeholder={languageController.getFormCopy('placeholder', 'subject')}
          options={formLang.select.emailSubjects.map(subject => ({
            value: subject[language],
            label: subject[language],
          }))}
        />
        <Textarea
          name="message"
          label={languageController.getFormCopy('label', 'message2500')}
          id="contact-message"
          value={message}
          isRequired={true}
          errorMessage={errors.message}
          onChange={handleChange}
          maxLength={1999}
          rows={5}
        />
        <Button
          type="submit"
          className="mz-btn--primary mz-btn--red"
          label={formSubmitButtonText ? formSubmitButtonText : languageController.getCopy('contact', 'submit')}
        />
      </Form>
    </FormContainer>
  )
}

export default ContactUs
